import type {
  Gig,
  EffectiveFeature,
  SongRequest,
  FreeformSuggestion,
  PerformerStatus,
  ErrorEnvelope,
} from '@stage/contracts';

/**
 * Authenticated admin API client (§28.4). Attaches `Authorization: Bearer <access token>` and a
 * per-request `x-request-id` correlation header, and parses the contracts {@link ErrorEnvelope}
 * into a typed {@link ApiError} on failure.
 *
 * The client never stores tokens: it reads the current access token through the injected getter so
 * it always uses the freshest in-memory value from {@link AuthContext} (ADR 0003).
 */

const BASE = '/v1';

/** Song requests and free-form suggestions share the {@link InteractionBase} performer surface. */
export type AdminInteraction = SongRequest | FreeformSuggestion;

export class ApiError extends Error {
  readonly status: number;
  readonly envelope: ErrorEnvelope | null;
  readonly retryAfterSeconds: number | null;
  constructor(status: number, envelope: ErrorEnvelope | null) {
    super(envelope?.error.message ?? `Request failed (${status})`);
    this.name = 'ApiError';
    this.status = status;
    this.envelope = envelope;
    this.retryAfterSeconds = envelope?.error.retryAfterSeconds ?? null;
  }
  /** True when the failure is an expired/absent session rather than a server or network fault. */
  get isAuthError(): boolean {
    return this.status === 401 || this.envelope?.error.code === 'AUTH_REQUIRED';
  }
  get fieldErrors() {
    return this.envelope?.error.fieldErrors ?? [];
  }
}

export function newRequestId(): string {
  const c = globalThis.crypto;
  if (c && 'randomUUID' in c) return `req-${c.randomUUID()}`;
  return `req-${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
}

export async function parseError(res: Response): Promise<ApiError> {
  let envelope: ErrorEnvelope | null = null;
  try {
    envelope = (await res.json()) as ErrorEnvelope;
  } catch {
    envelope = null;
  }
  return new ApiError(res.status, envelope);
}

export interface AdminClient {
  listGigs(artistId: string): Promise<Gig[]>;
  getGig(gigId: string): Promise<Gig>;
  getFeatures(gigId: string): Promise<EffectiveFeature[]>;
  publishGig(gigId: string): Promise<Gig>;
  startGig(gigId: string): Promise<Gig>;
  intermission(gigId: string): Promise<Gig>;
  resumeGig(gigId: string): Promise<Gig>;
  endGig(gigId: string): Promise<Gig>;
  patchLiveState(gigId: string, patch: LiveStatePatch): Promise<Gig>;
  listInteractions(gigId: string, updatedAfter?: string): Promise<InteractionsPage>;
  patchInteraction(
    gigId: string,
    interactionId: string,
    patch: InteractionPatch,
  ): Promise<AdminInteraction>;
}

/** Subset of live-session fields a performer can set from the console (§24.2). */
export interface LiveStatePatch {
  customNowPlaying?: string | null;
  currentSetlistItemId?: string | null;
  currentInteractionId?: string | null;
}

/** Performer moves an interaction through its workflow (§18.7, §24.3). */
export interface InteractionPatch {
  performerStatus: PerformerStatus;
  /** Optimistic-concurrency guard echoed back from the queue row (§13.3). */
  version?: number;
}

export interface InteractionsPage {
  interactions: AdminInteraction[];
  /** Server-provided high-water mark for the next `updatedAfter` poll (§24.4). */
  updatedAfter: string | null;
  etag: string | null;
}

/**
 * Create an admin client bound to a live access-token getter. The getter is called per request so
 * silent refresh (§32.3) is transparent to callers.
 */
export function createAdminClient(getAccessToken: () => string | null): AdminClient {
  async function request<T>(path: string, init?: RequestInit): Promise<T> {
    const token = getAccessToken();
    const headers = new Headers(init?.headers);
    headers.set('accept', 'application/json');
    headers.set('x-request-id', newRequestId());
    if (token) headers.set('authorization', `Bearer ${token}`);
    if (init?.body && !headers.has('content-type')) {
      headers.set('content-type', 'application/json');
    }
    const res = await fetch(`${BASE}${path}`, { ...init, headers });
    if (!res.ok) throw await parseError(res);
    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  }

  const post = <T>(path: string) => request<T>(path, { method: 'POST' });

  return {
    listGigs: (artistId) => request<Gig[]>(`/artists/${encodeURIComponent(artistId)}/gigs`),
    getGig: (gigId) => request<Gig>(`/gigs/${encodeURIComponent(gigId)}`),
    getFeatures: (gigId) =>
      request<EffectiveFeature[]>(`/gigs/${encodeURIComponent(gigId)}/features`),
    publishGig: (gigId) => post<Gig>(`/gigs/${encodeURIComponent(gigId)}/publish`),
    startGig: (gigId) => post<Gig>(`/gigs/${encodeURIComponent(gigId)}/start`),
    intermission: (gigId) => post<Gig>(`/gigs/${encodeURIComponent(gigId)}/intermission`),
    resumeGig: (gigId) => post<Gig>(`/gigs/${encodeURIComponent(gigId)}/resume`),
    endGig: (gigId) => post<Gig>(`/gigs/${encodeURIComponent(gigId)}/end`),
    patchLiveState: (gigId, patch) =>
      request<Gig>(`/gigs/${encodeURIComponent(gigId)}/live-state`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    async listInteractions(gigId, updatedAfter) {
      const qs = updatedAfter ? `?updatedAfter=${encodeURIComponent(updatedAfter)}` : '';
      const token = getAccessToken();
      const headers = new Headers();
      headers.set('accept', 'application/json');
      headers.set('x-request-id', newRequestId());
      if (token) headers.set('authorization', `Bearer ${token}`);
      const res = await fetch(`${BASE}/gigs/${encodeURIComponent(gigId)}/interactions${qs}`, {
        headers,
      });
      if (!res.ok) throw await parseError(res);
      const interactions = (await res.json()) as AdminInteraction[];
      return {
        interactions,
        updatedAfter: res.headers.get('x-updated-after') ?? highWaterMark(interactions),
        etag: res.headers.get('etag'),
      };
    },
    patchInteraction: (gigId, interactionId, patch) =>
      request<AdminInteraction>(
        `/gigs/${encodeURIComponent(gigId)}/interactions/${encodeURIComponent(interactionId)}`,
        { method: 'PATCH', body: JSON.stringify(patch) },
      ),
  };
}

/** Fallback cursor when the server omits `x-updated-after`: latest `updatedAt` we have seen. */
function highWaterMark(interactions: AdminInteraction[]): string | null {
  let max: string | null = null;
  for (const it of interactions) {
    if (!max || it.updatedAt > max) max = it.updatedAt;
  }
  return max;
}
